const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/** Camera-relative assistance in CSS pixels. Candidates have already passed
 * exact visibility/contact checks at the graphics boundary. This pure owner
 * chooses the nearest requested target, never its strategic value. dt is active
 * seconds; release freezes the offset, not a moving target. Invalid input resets
 * safely. World-ray correction is independently capped by the caller. */
export function createSkySightAssist() {
  let offset = { x: 0, y: 0 },
    previous = null,
    retained = null;
  return {
    reset() {
      offset = { x: 0, y: 0 };
      previous = null;
      retained = null;
    },
    sample(request, candidates, { width, height, dt = 0, active = false, level = 'standard' }) {
      if (
        ![request?.x, request?.y, width, height].every(Number.isFinite) ||
        width <= 0 ||
        height <= 0
      ) {
        offset = { x: 0, y: 0 };
        retained = null;
        previous = null;
        return { x: 0, y: 0, engaged: false };
      }
      dt = Number.isFinite(dt) ? clamp(dt, 0, 0.1) : 0;
      const envelope =
        clamp(Math.min(width, height) * 0.025, 12, 24) * (level === 'light' ? 0.5 : 1);
      if (level === 'off') {
        offset = { x: 0, y: 0 };
        retained = null;
        previous = { ...request };
        return { ...request, engaged: false };
      }
      const eligible = (Array.isArray(candidates) ? candidates : [])
        .filter((c) => [c?.x, c?.y].every(Number.isFinite) && c.id != null)
        .map((c) => ({
          ...c,
          distance: Math.hypot(((c.x - request.x) * width) / 2, ((c.y - request.y) * height) / 2),
        }))
        .filter((c) => c.distance <= envelope)
        .sort((a, b) => a.distance - b.distance || String(a.id).localeCompare(String(b.id)));
      const nearest = eligible[0];
      const held = eligible.find((c) => c.id === retained);
      const target = held && (!nearest || held.distance <= nearest.distance + 2) ? held : nearest;
      // Occlusion/unavailability releases immediately, including on a held finger.
      if (!target) {
        if (active) offset = { x: 0, y: 0 };
        retained = null;
      } else if (active) {
        const alpha = 1 - Math.exp(-clamp(dt, 0, 0.1) * 14);
        const friction = previous && dt > 0 ? 0.18 / (dt * 60) : 0;
        const desired = {
          x: (target.x - request.x) * 0.42 - (request.x - (previous?.x ?? request.x)) * friction,
          y: (target.y - request.y) * 0.42 - (request.y - (previous?.y ?? request.y)) * friction,
        };
        offset.x += (desired.x - offset.x) * alpha;
        offset.y += (desired.y - offset.y) * alpha;
        retained = target.id;
      }
      // Large pointer jumps and a narrower resize cannot leave a smoothed
      // correction outside the same CSS-pixel acquisition envelope.
      const correction = Math.hypot((offset.x * width) / 2, (offset.y * height) / 2);
      if (correction > envelope) {
        offset.x *= envelope / correction;
        offset.y *= envelope / correction;
      }
      previous = { ...request };
      return {
        x: clamp(request.x + offset.x, -0.96, 0.96),
        y: clamp(request.y + offset.y, -0.96, 0.96),
        engaged: retained !== null && !!target && Math.hypot(offset.x, offset.y) > 1e-6,
        targetId: retained,
      };
    },
  };
}
